import * as React from "react";
import { observer } from "mobx-react";
import {
  ModalEnum,
  modalStore,
  seeTaskEventsModalStore,
} from "../../store";
import axios from "axios";
import call from "../../call";
import downloadBlob from "../../download";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Title from "./Title";
import {Pagination} from "@mui/lab";

const DownloadButton = observer((props) => {
  const handleDownloadButtonClick = () => {
      call(
        axios({
          method: "get",
          url: `/api/g2/task_download/${props.taskId}`,
          headers: {
            "content-type": "application/json",
            accept: "application/csv",
          },
          params: {
            task_id: props.taskId,
          },
          responseType: "blob",
        }),
      ).then((resp) => {
          const dateFrom = new Date(props.task.dateFrom).toISOString().split('T')[0];
          const dateTo = new Date(props.task.dateTo).toISOString().split('T')[0];
          const clientName = (props.task.client?.name || '')
            // Replace any character that's not a letter, number, space, hyphen, or underscore
            .replaceAll(/[^\w\s-]/g, '')
            // Replace spaces with underscores
            .replaceAll(/\s+/g, '_')
            // Remove any remaining consecutive underscores
            .replaceAll(/__+/g, '_')
            // Remove leading/trailing underscores
            .replaceAll(/(?:^_)|(?:_$)/g, '');
          const filename = clientName + '_' + dateFrom + '_' + dateTo + '.csv';
          downloadBlob(filename, resp.data);
      });
  };

  return (
    <Button
      variant={props.variant}
      onClick={handleDownloadButtonClick}
      disabled={props.disabled}
    >
      Download
    </Button>
  );
});

const EventsButton = observer((props) => {
  const handleSeeEventsButtonClick = () => {
    seeTaskEventsModalStore.setTaskId(props.taskId);
    modalStore.setModal(ModalEnum.SEE_TASK_EVENTS);
  };

  return (
    <Button
      variant={props.variant}
      onClick={handleSeeEventsButtonClick}
      disabled={props.disabled}
    >
      Events
    </Button>
  );
});

const Tasks = observer(() => {
  const [tasks, setTasks] = React.useState({ data: [], meta: { total: 0, page: 1, limit: 10 } });
  const [page, setPage] = React.useState(1);

  const update = (newPage) => {
      call(
          axios({
              method: "get",
              url: "/api/g2/task",
              params: { 
                page: newPage,
                limit: 10
              },
              headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
              },
          }),
      ).then((resp) => {
          setTasks({
              data: resp.data.data,
              meta: resp.data.meta
          });
      });
  }

  React.useEffect(() => {
    update(page);
    const interval = setInterval(() => update(page), 2000);
    return () => {
      clearInterval(interval);
    };
  }, [page]);

  const tableCellStyle = {
    paddingTop: "13px",
  };

  return (
    <React.Fragment>
      <Title>Your Recent Tasks</Title>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Created At</TableCell>
            <TableCell>Finished At</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>CID</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Progress</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tasks.data.map((task) => (
            <TableRow key={task.id} sx={{ verticalAlign: "top" }}>
              <TableCell sx={tableCellStyle}>
                {new Date(task.createdAt).toLocaleString()}
              </TableCell>
              <TableCell sx={tableCellStyle}>
                {task.status === 2 && new Date(task.updatedAt).toLocaleString()}
              </TableCell>
              <TableCell sx={tableCellStyle}>{task.client?.name || 'N/A'}</TableCell>
              <TableCell>
                <Table size="small">
                  <TableBody>
                    {task.cids?.map((cid) => (
                      <TableRow key={cid}>
                        <TableCell sx={{ paddingLeft: 0, borderBottom: "none" }}>{cid}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableCell>
              <TableCell sx={tableCellStyle}>{task.statusLabel || 'Unknown'}</TableCell>
              <TableCell sx={tableCellStyle}>{task.progress || 0}%</TableCell>
              <TableCell align="right">
                <DownloadButton
                  variant="text"
                  taskId={task.id}
                  task={task}
                  disabled={task.status !== 2} // 2 is the status code for 'Done'
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
        <Pagination
          layout="prev, pager, next"
          count={tasks.meta?.totalPages || 1}
          page={page}
          pageSize={tasks.meta?.limit || 10}
          onChange={(event, newPage) => {
              setPage(newPage)
          }}
        />

    </React.Fragment>
  );
});

export default Tasks;
